<template>
  <section class="waajo-sessions">
    <div class="waajo-session-card">
      <div class="waajo-session-card-head">
        <div>
          <h4>
            Daftar Sesi
            <span v-if="totalRecords > 0" class="waajo-session-count">
              {{ totalRecords }} sesi
            </span>
          </h4>
          <p>Kelola percakapan pelanggan yang ditangani AI pada seluruh lisensi Anda.</p>
        </div>
        <button
          type="button"
          class="btn waajo-session-refresh"
          :disabled="loading"
          title="Muat ulang daftar sesi"
          @click="loadItems"
        >
          <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
        </button>
      </div>

      <div class="waajo-session-toolbar">
        <div class="waajo-session-search">
          <i class="fa fa-search" aria-hidden="true"></i>
          <input
            v-model="filters.keywords"
            type="text"
            autocomplete="off"
            placeholder="Cari nomor pelanggan atau isi pesan..."
            aria-label="Cari nomor pelanggan atau isi pesan"
            @keyup.enter="searchData"
          />
          <button
            v-if="filters.keywords"
            type="button"
            class="waajo-session-search-clear"
            title="Bersihkan pencarian"
            aria-label="Bersihkan pencarian"
            @click="clearSearch"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <button type="button" class="btn waajo-session-search-btn" @click="searchData">
          <i class="fa fa-search"></i>
          <span>Cari</span>
        </button>

        <select
          v-model="filters.status"
          class="waajo-session-select"
          aria-label="Filter status sesi"
          @change="searchData"
        >
          <option value="">Semua status</option>
          <option value="running">Running</option>
          <option value="paused">Paused</option>
          <option value="closed">Closed</option>
        </select>

        <div class="waajo-session-date-range" :class="{ 'is-active': hasDateFilter }">
          <i class="fa fa-calendar" aria-hidden="true"></i>
          <input
            v-model="filters.startDate"
            type="date"
            :max="filters.endDate || null"
            title="Tanggal mulai"
            aria-label="Tanggal mulai"
            @change="searchData"
          />
          <span class="waajo-session-date-sep">&ndash;</span>
          <input
            v-model="filters.endDate"
            type="date"
            :min="filters.startDate || null"
            title="Tanggal akhir"
            aria-label="Tanggal akhir"
            @change="searchData"
          />
          <button
            v-if="hasDateFilter"
            type="button"
            class="waajo-session-date-clear"
            title="Hapus rentang waktu"
            aria-label="Hapus rentang waktu"
            @click="clearDateRange"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <button
          type="button"
          class="btn waajo-session-export"
          :disabled="exporting"
          @click="exportExcel"
        >
          <i :class="exporting ? 'fa fa-spinner fa-spin' : 'fa fa-file-excel-o'"></i>
          <span>{{ exporting ? "Menyiapkan..." : "Export Excel" }}</span>
        </button>

        <button
          v-if="hasActiveFilters"
          type="button"
          class="btn waajo-session-reset"
          @click="resetFilters"
        >
          Reset filter
        </button>
      </div>

      <div v-if="downloadUrl" class="waajo-session-alert is-success">
        <span>
          <i class="fa fa-check-circle"></i>
          File Excel berhasil dibuat.
        </span>
        <a :href="downloadUrl" target="_blank" rel="noopener noreferrer">Buka file</a>
        <button type="button" title="Tutup" @click="downloadUrl = ''">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div v-if="errorMessage" class="waajo-session-alert is-error">
        <span>
          <i class="fa fa-exclamation-circle"></i>
          {{ errorMessage }}
        </span>
        <button type="button" @click="loadItems">Coba lagi</button>
      </div>

      <div class="waajo-session-table-wrap">
        <table class="table waajo-session-table mb-0">
          <thead>
            <tr>
              <th class="waajo-session-check-col">
                <input
                  v-model="selectAllTable"
                  type="checkbox"
                  :disabled="loading || !rows.length"
                  title="Pilih semua sesi pada halaman ini"
                  aria-label="Pilih semua sesi pada halaman ini"
                />
              </th>
              <th>Waktu</th>
              <th>Via AI Chat</th>
              <th>Lisensi</th>
              <th>No. Pelanggan</th>
              <th>Status</th>
              <th>Berakhir</th>
              <th>Pesan Terakhir</th>
              <th class="text-right">Aksi</th>
            </tr>
          </thead>

          <tbody>
            <template v-if="loading">
              <tr v-for="n in 6" :key="'skeleton-' + n">
                <td class="waajo-session-check-col">
                  <span class="waajo-session-sk waajo-session-sk-check"></span>
                </td>
                <td><span class="waajo-session-sk" style="width: 112px;"></span></td>
                <td><span class="waajo-session-sk" style="width: 92px;"></span></td>
                <td><span class="waajo-session-sk" style="width: 122px;"></span></td>
                <td><span class="waajo-session-sk" style="width: 128px;"></span></td>
                <td><span class="waajo-session-sk waajo-session-sk-badge"></span></td>
                <td><span class="waajo-session-sk" style="width: 110px;"></span></td>
                <td><span class="waajo-session-sk" style="width: 190px;"></span></td>
                <td><span class="waajo-session-sk" style="width: 154px; margin-left: auto;"></span></td>
              </tr>
            </template>

            <template v-else-if="rows.length">
              <tr
                v-for="(row, index) in rows"
                :key="rowKey(row, index)"
                :class="{ 'is-selected': isSelected(row) }"
              >
                <td class="waajo-session-check-col">
                  <input
                    v-model="selectedRows"
                    type="checkbox"
                    :value="row"
                    :aria-label="'Pilih sesi ' + (row.message_number || '')"
                  />
                </td>

                <td class="waajo-session-nowrap">
                  <span class="waajo-session-date">{{ formatDate(sessionDate(row)) }}</span>
                </td>

                <td class="waajo-session-nowrap">
                  <span class="waajo-session-source">{{ row.autoreply_title || "—" }}</span>
                </td>

                <td class="waajo-session-nowrap">
                  <span class="waajo-session-license">
                    <i class="fa fa-shield"></i>
                    {{ row.license_phone_number || "-" }}
                  </span>
                </td>

                <td class="waajo-session-nowrap">
                  <button
                    type="button"
                    class="waajo-session-phone"
                    :disabled="chatOpening"
                    :title="'Buka percakapan ' + (row.message_number || '') + ' di Chat Room'"
                    @click="openChat(row)"
                  >
                    <i :class="isChatOpening(row) ? 'fa fa-spinner fa-spin' : 'fa fa-whatsapp'"></i>
                    {{ row.message_number || "-" }}
                  </button>
                </td>

                <td>
                  <span class="waajo-session-status" :class="statusClass(row)">
                    <span class="waajo-session-status-dot"></span>
                    {{ statusText(row) }}
                  </span>
                  <small v-if="statusNote(row)" class="waajo-session-status-note">
                    {{ statusNote(row) }}
                  </small>
                </td>

                <td class="waajo-session-nowrap">
                  <span class="waajo-session-end">{{ formatExpire(row.expire_datetime) }}</span>
                </td>

                <td>
                  <button
                    type="button"
                    class="waajo-session-message"
                    :title="lastMessage(row) || 'Belum ada pesan'"
                    @click="openDetail(row)"
                  >
                    {{ lastMessage(row) || "Belum ada pesan" }}
                  </button>
                </td>

                <td>
                  <div class="waajo-session-actions">
                    <button
                      type="button"
                      class="btn waajo-session-detail-btn"
                      :disabled="actionBusy"
                      @click="openDetail(row)"
                    >
                      <i class="fa fa-eye"></i>
                      Detail
                    </button>
                    <button
                      type="button"
                      class="btn waajo-session-more-btn"
                      :disabled="actionBusy || !row.id"
                      :aria-expanded="rowMenu.open && rowMenu.row && rowMenu.row.id === row.id ? 'true' : 'false'"
                      aria-haspopup="true"
                      @click.stop="toggleRowMenu(row, $event)"
                    >
                      <i :class="isRowBusy(row) ? 'fa fa-spinner fa-spin' : 'fa fa-ellipsis-h'"></i>
                      <span>Lainnya</span>
                      <i class="fa fa-angle-down"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </template>

            <template v-else>
              <tr>
                <td colspan="9">
                  <div class="waajo-session-empty">
                    <span class="waajo-session-empty-icon">
                      <i class="fa fa-comments-o"></i>
                    </span>
                    <strong>Belum ada sesi AI Chat</strong>
                    <p>
                      Sesi akan muncul setelah AI mulai menangani percakapan pelanggan.
                      Ubah kata kunci atau filter bila sesi yang dicari tidak tampil.
                    </p>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="!loading && totalRecords > 0" class="waajo-session-footer">
        <div class="waajo-session-page-size">
          <span>Tampilkan</span>
          <select
            v-model.number="perPage"
            aria-label="Jumlah sesi per halaman"
            @change="onPerPageChange"
          >
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="30">30</option>
            <option :value="40">40</option>
            <option :value="50">50</option>
          </select>
          <span>per halaman</span>
        </div>

        <div class="waajo-session-page-info">
          Menampilkan {{ rangeStart }}&ndash;{{ rangeEnd }} dari {{ totalRecords }} sesi
        </div>

        <div class="waajo-session-pager">
          <button
            type="button"
            :disabled="currentPage <= 1"
            aria-label="Halaman sebelumnya"
            @click="goToPage(currentPage - 1)"
          >
            <i class="fa fa-angle-left"></i>
          </button>

          <template v-for="(pageItem, index) in pageNumbers">
            <span
              v-if="pageItem === '...'"
              :key="'ellipsis-' + index"
              class="waajo-session-pager-ellipsis"
            >
              &hellip;
            </span>
            <button
              v-else
              :key="'page-' + pageItem"
              type="button"
              :class="{ active: pageItem === currentPage }"
              @click="goToPage(pageItem)"
            >
              {{ pageItem }}
            </button>
          </template>

          <button
            type="button"
            :disabled="currentPage >= totalPages"
            aria-label="Halaman berikutnya"
            @click="goToPage(currentPage + 1)"
          >
            <i class="fa fa-angle-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Floating bulk action, mengikuti pola halaman Kontak -->
    <transition name="waajo-session-slide-up">
      <div v-if="selectedRows.length" class="waajo-session-bulk">
        <div class="waajo-session-bulk-count">
          <span class="waajo-session-bulk-pill">{{ selectedRows.length }}</span>
          <span>Sesi Dipilih</span>
        </div>

        <span class="waajo-session-bulk-sep"></span>

        <button
          type="button"
          class="waajo-session-bulk-btn"
          :disabled="!canBulkDestroy || actionBusy"
          :title="canBulkDestroy ? '' : bulkDisabledReason('destroy')"
          @click="runAction('destroy', selectedRows)"
        >
          <i class="fa fa-stop"></i>
          <span>Berhenti</span>
        </button>

        <button
          type="button"
          class="waajo-session-bulk-btn"
          :disabled="!canBulkPause || actionBusy"
          :title="canBulkPause ? '' : bulkDisabledReason('pause')"
          @click="runAction('pause', selectedRows)"
        >
          <i class="fa fa-pause"></i>
          <span>Jeda</span>
        </button>

        <button
          type="button"
          class="waajo-session-bulk-btn"
          :disabled="!canBulkStart || actionBusy"
          :title="canBulkStart ? '' : bulkDisabledReason('start')"
          @click="runAction('start', selectedRows)"
        >
          <i class="fa fa-play"></i>
          <span>Mulai Ulang</span>
        </button>

        <button
          type="button"
          class="waajo-session-bulk-btn is-danger"
          :disabled="actionBusy"
          @click="runAction('delete', selectedRows)"
        >
          <i class="fa fa-trash"></i>
          <span>Hapus</span>
        </button>

        <button
          type="button"
          class="waajo-session-bulk-btn is-clear"
          :disabled="actionBusy"
          @click="clearSelection"
        >
          <i class="fa fa-times"></i>
          <span>Bersihkan Pilihan</span>
        </button>
      </div>
    </transition>

    <!-- Menu aksi per sesi, diposisikan fixed agar tidak terpotong tabel -->
    <div
      v-if="rowMenu.open && rowMenu.row"
      class="waajo-session-menu"
      :style="rowMenu.style"
      role="menu"
      @click.stop
    >
      <button type="button" class="waajo-session-menu-item" @click="openDetail(rowMenu.row)">
        <i class="fa fa-eye"></i>
        <span>Detail</span>
      </button>
      <button
        type="button"
        class="waajo-session-menu-item"
        :disabled="chatOpening"
        @click="openChat(rowMenu.row)"
      >
        <i class="fa fa-comments-o"></i>
        <span>Lihat Chat</span>
      </button>

      <div
        v-if="canDestroy(rowMenu.row) || canPause(rowMenu.row) || canStart(rowMenu.row)"
        class="waajo-session-menu-divider"
      ></div>

      <button
        v-if="canDestroy(rowMenu.row)"
        type="button"
        class="waajo-session-menu-item"
        :disabled="actionBusy"
        @click="runAction('destroy', [rowMenu.row])"
      >
        <i class="fa fa-stop"></i>
        <span>Berhenti</span>
      </button>

      <button
        v-if="canPause(rowMenu.row)"
        type="button"
        class="waajo-session-menu-item"
        :disabled="actionBusy"
        @click="runAction('pause', [rowMenu.row])"
      >
        <i class="fa fa-pause"></i>
        <span>Jeda</span>
      </button>

      <button
        v-if="canStart(rowMenu.row)"
        type="button"
        class="waajo-session-menu-item"
        :disabled="actionBusy"
        @click="runAction('start', [rowMenu.row])"
      >
        <i class="fa fa-play"></i>
        <span>Mulai Ulang</span>
      </button>

      <div class="waajo-session-menu-divider"></div>

      <button
        type="button"
        class="waajo-session-menu-item is-danger"
        :disabled="actionBusy"
        @click="runAction('delete', [rowMenu.row])"
      >
        <i class="fa fa-trash"></i>
        <span>Hapus</span>
      </button>
    </div>

    <!-- Detail sesi -->
    <transition name="waajo-session-fade">
      <div v-if="detailRow" class="waajo-session-modal-backdrop" @click.self="closeDetail">
        <div class="waajo-session-modal" role="dialog" aria-modal="true" aria-label="Detail sesi AI Chat">
          <div class="waajo-session-modal-head">
            <div>
              <span class="waajo-session-modal-eyebrow">Detail Sesi</span>
              <h4>{{ detailRow.message_number || "Tanpa nomor" }}</h4>
            </div>
            <button type="button" class="waajo-session-modal-close" @click="closeDetail">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="waajo-session-modal-body">
            <div class="waajo-session-detail-grid">
              <div class="waajo-session-detail-item">
                <span>No. Pelanggan</span>
                <strong>{{ detailRow.message_number || "-" }}</strong>
              </div>
              <div class="waajo-session-detail-item">
                <span>Lisensi</span>
                <strong>{{ detailRow.license_phone_number || "-" }}</strong>
              </div>
              <div class="waajo-session-detail-item">
                <span>Waktu Dibuat</span>
                <strong>{{ formatDate(sessionDate(detailRow)) }}</strong>
              </div>
              <div class="waajo-session-detail-item">
                <span>Berakhir</span>
                <strong>{{ formatExpire(detailRow.expire_datetime) }}</strong>
              </div>
              <div class="waajo-session-detail-item">
                <span>Status</span>
                <span class="waajo-session-status" :class="statusClass(detailRow)">
                  <span class="waajo-session-status-dot"></span>
                  {{ statusText(detailRow) }}
                </span>
                <small v-if="statusNote(detailRow)" class="waajo-session-status-note">
                  {{ statusNote(detailRow) }}
                </small>
              </div>
              <div class="waajo-session-detail-item">
                <span>Via AI Chat</span>
                <strong>{{ detailRow.autoreply_title || "-" }}</strong>
              </div>
            </div>

            <div class="waajo-session-detail-section">
              <div class="waajo-session-detail-section-head">
                <h5>Pesan Terakhir</h5>
                <small>ID Sesi: {{ detailRow.id }}</small>
              </div>

              <div v-if="detailMessages.length" class="waajo-session-transcript">
                <div
                  v-for="(message, index) in detailMessages"
                  :key="'transcript-' + index"
                  class="waajo-session-bubble"
                  :class="transcriptRoleClass(message)"
                >
                  <span class="waajo-session-bubble-label">{{ transcriptRoleLabel(message) }}</span>
                  <p>{{ message.content }}</p>
                </div>
              </div>
              <div v-else class="waajo-session-detail-empty">
                Belum ada pesan yang dapat ditampilkan pada sesi ini.
              </div>
            </div>

            <div v-if="detailSummary" class="waajo-session-detail-section">
              <h5>Rangkuman Percakapan</h5>
              <p class="waajo-session-summary">{{ detailSummary }}</p>
            </div>

            <div class="waajo-session-detail-note">
              <i class="fa fa-info-circle"></i>
              <span>
                Daftar sesi hanya memuat pesan terakhir. Riwayat lengkap tersedia di
                Chat Room melalui tombol <b>Lihat Chat</b>.
              </span>
            </div>
          </div>

          <div class="waajo-session-modal-foot">
            <button type="button" class="btn waajo-session-btn-secondary" @click="closeDetail">
              Tutup
            </button>
            <button
              type="button"
              class="btn waajo-session-btn-primary"
              :disabled="chatOpening"
              @click="openChat(detailRow)"
            >
              <i :class="chatOpening ? 'fa fa-spinner fa-spin' : 'fa fa-comments-o'"></i>
              Lihat Chat
            </button>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import moment from "moment";
import { openChatRoomForSession } from "@/utils/chatRoom";

export default {
  name: "aiListSessions",
  data() {
    return {
      rows: [],
      totalRecords: 0,
      currentPage: 1,
      perPage: 10,
      loading: false,
      exporting: false,
      actionBusy: false,
      chatOpening: false,
      chatOpeningRowId: "",
      processingAction: "",
      processingRowIds: [],
      requestId: 0,
      nowTick: Date.now(),
      downloadUrl: "",
      errorMessage: "",
      selectedRows: [],
      detailRow: null,
      rowMenu: {
        open: false,
        row: null,
        style: {}
      },
      filters: {
        keywords: "",
        status: "",
        startDate: "",
        endDate: ""
      }
    };
  },
  computed: {
    selectAllTable: {
      get() {
        return this.rows.length > 0 && this.selectedRows.length === this.rows.length;
      },
      set(value) {
        this.selectedRows = value ? this.rows.slice() : [];
      }
    },
    hasDateFilter() {
      return !!(this.filters.startDate || this.filters.endDate);
    },
    hasActiveFilters() {
      return !!(
        this.filters.keywords ||
        this.filters.status ||
        this.filters.startDate ||
        this.filters.endDate
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRecords / this.perPage));
    },
    rangeStart() {
      return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    },
    rangeEnd() {
      return Math.min(this.totalRecords, this.currentPage * this.perPage);
    },
    pageNumbers() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) {
        return Array.from({ length: total }, (_, index) => index + 1);
      }

      const pages = [1];
      if (current > 3) {
        pages.push("...");
      }
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let page = start; page <= end; page++) {
        pages.push(page);
      }
      if (current < total - 2) {
        pages.push("...");
      }
      pages.push(total);
      return pages;
    },
    detailMessages() {
      return this.transcriptOf(this.detailRow);
    },
    detailSummary() {
      const row = this.detailRow || {};
      return row.ai_user_summary || row.user_summary || row.summary || "";
    },
    canBulkPause() {
      return (
        this.selectedRows.length > 0 &&
        this.selectedRows.every(row => this.canPause(row))
      );
    },
    canBulkDestroy() {
      return (
        this.selectedRows.length > 0 &&
        this.selectedRows.every(row => this.canDestroy(row))
      );
    },
    canBulkStart() {
      return (
        this.selectedRows.length > 0 &&
        this.selectedRows.every(row => this.canStart(row))
      );
    }
  },
  created() {
    this.loadItems();
  },
  mounted() {
    this._nowTimer = setInterval(() => {
      this.nowTick = Date.now();
    }, 60000);
    document.addEventListener("click", this.handleDocumentClick);
    window.addEventListener("resize", this.closeRowMenu);
    window.addEventListener("scroll", this.closeRowMenu, true);
  },
  beforeDestroy() {
    if (this._nowTimer) {
      clearInterval(this._nowTimer);
    }
    document.removeEventListener("click", this.handleDocumentClick);
    window.removeEventListener("resize", this.closeRowMenu);
    window.removeEventListener("scroll", this.closeRowMenu, true);
  },
  methods: {
    rowKey(row, index) {
      return row && row.id ? row.id : "session-" + index;
    },
    sessionDate(row) {
      if (!row) {
        return "";
      }
      return row.create_datetime || row.update_datetime || "";
    },
    formatDate(value) {
      const timestamp = Number(value);
      if (!timestamp || isNaN(timestamp)) {
        return "\u2014";
      }
      return moment.unix(timestamp).format("DD MMM YYYY, HH:mm");
    },
    formatExpire(value) {
      const timestamp = Number(value);
      if (!timestamp || isNaN(timestamp)) {
        return "\u2014";
      }
      return moment.unix(timestamp).format("DD MMM YYYY, HH:mm");
    },
    statusKey(row) {
      if (!row) {
        return "running";
      }
      if (Number(row.is_destroy) === 1) {
        return "closed";
      }
      if (Number(row.is_paused) === 1) {
        return "paused";
      }
      return "running";
    },
    statusText(row) {
      const status = this.statusKey(row);
      if (status === "closed") {
        return "Closed";
      }
      if (status === "paused") {
        return "Paused";
      }
      return "Running";
    },
    statusClass(row) {
      return "is-" + this.statusKey(row);
    },
    statusNote(row) {
      return this.isPreparingSummary(row) ? "Menyiapkan rangkuman" : "";
    },
    isPreparingSummary(row) {
      if (this.statusKey(row) !== "running") {
        return false;
      }
      const expire = Number(row && row.expire_datetime);
      return expire > 0 && expire * 1000 < this.nowTick;
    },
    transcriptOf(row) {
      if (!row) {
        return [];
      }
      let transcript = row.transcript;
      if (typeof transcript === "string") {
        try {
          transcript = JSON.parse(transcript);
        } catch (error) {
          transcript = [];
        }
      }
      if (!Array.isArray(transcript)) {
        return [];
      }
      return transcript.filter(item => {
        return item && item.content && String(item.role || "").toLowerCase() !== "system";
      });
    },
    lastMessage(row) {
      const messages = this.transcriptOf(row);
      if (!messages.length) {
        return "";
      }
      return String(messages[messages.length - 1].content || "").trim();
    },
    transcriptRoleLabel(message) {
      const role = String((message && message.role) || "").toLowerCase();
      if (role === "assistant") {
        return "AI";
      }
      if (role === "user") {
        return "Pelanggan";
      }
      return "Pesan";
    },
    transcriptRoleClass(message) {
      const role = String((message && message.role) || "").toLowerCase();
      return role === "assistant" ? "is-assistant" : "is-user";
    },
    isSelected(row) {
      return this.selectedRows.indexOf(row) !== -1;
    },
    isRowBusy(row) {
      if (!row || !this.processingRowIds.length) {
        return false;
      }
      return this.processingRowIds.indexOf(String(row.id)) !== -1;
    },
    isChatOpening(row) {
      return !!(
        this.chatOpening &&
        row &&
        String(this.chatOpeningRowId) === String(row.id)
      );
    },
    canPause(row) {
      return this.statusKey(row) === "running" && !this.isPreparingSummary(row);
    },
    canDestroy(row) {
      return this.statusKey(row) !== "closed" && !this.isPreparingSummary(row);
    },
    canStart(row) {
      return this.statusKey(row) === "paused";
    },
    bulkDisabledReason(action) {
      if (action === "pause") {
        return "Jeda hanya dapat dilakukan bila semua sesi berstatus Running.";
      }
      if (action === "destroy") {
        return "Berhenti hanya dapat dilakukan bila semua sesi berstatus Running atau Paused.";
      }
      if (action === "start") {
        return "Mulai Ulang hanya dapat dilakukan bila semua sesi berstatus Paused.";
      }
      return "";
    },
    statusFilterParams() {
      if (this.filters.status === "running") {
        return { is_destroy: "0", is_paused: "0" };
      }
      if (this.filters.status === "paused") {
        return { is_destroy: "0", is_paused: "1" };
      }
      if (this.filters.status === "closed") {
        return { is_destroy: "1", is_paused: "" };
      }
      return { is_destroy: "", is_paused: "" };
    },
    dateToUnix(dateValue, endOfDay) {
      if (!dateValue) {
        return "";
      }
      const parts = String(dateValue).split("-");
      if (parts.length !== 3) {
        return "";
      }
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      if (!year || !month || !day) {
        return "";
      }
      const date = new Date(
        year,
        month - 1,
        day,
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        0
      );
      return String(Math.floor(date.getTime() / 1000));
    },
    buildServerParams() {
      const statusParams = this.statusFilterParams();
      return {
        page: (this.currentPage - 1) * this.perPage,
        perPage: this.perPage,
        columnFilters: {
          message_number: this.filters.keywords.trim(),
          is_destroy: statusParams.is_destroy,
          is_paused: statusParams.is_paused,
          start_date: this.dateToUnix(this.filters.startDate, false),
          end_date: this.dateToUnix(this.filters.endDate, true)
        }
      };
    },
    async loadItems() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.$store.dispatch(
          "aiKey/listSessions",
          this.buildServerParams()
        );
        if (requestId !== this.requestId) {
          return;
        }
        const payload = response && response.data ? response.data : {};
        const rows = Array.isArray(payload.data) ? payload.data : [];
        const total = Number(payload.totalRecords);
        this.rows = rows;
        this.totalRecords = isNaN(total) ? rows.length : total;

        if (!rows.length && this.totalRecords > 0 && this.currentPage > 1) {
          this.currentPage = Math.max(1, Math.ceil(this.totalRecords / this.perPage));
          this.loadItems();
        }
      } catch (error) {
        if (requestId !== this.requestId) {
          return;
        }
        this.rows = [];
        this.totalRecords = 0;
        this.errorMessage = this.getErrorMessage(
          error,
          "Daftar sesi belum dapat dimuat. Periksa koneksi lalu coba lagi."
        );
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    searchData() {
      if (
        this.filters.startDate &&
        this.filters.endDate &&
        this.filters.startDate > this.filters.endDate
      ) {
        this.$fire({
          title: "Rentang waktu tidak valid",
          text: "Tanggal akhir harus sama atau setelah tanggal mulai.",
          type: "warning",
          timer: 2600
        });
        return;
      }
      this.currentPage = 1;
      this.clearSelection();
      this.loadItems();
    },
    clearSearch() {
      this.filters.keywords = "";
      this.searchData();
    },
    clearDateRange() {
      this.filters.startDate = "";
      this.filters.endDate = "";
      this.searchData();
    },
    resetFilters() {
      this.filters.keywords = "";
      this.filters.status = "";
      this.filters.startDate = "";
      this.filters.endDate = "";
      this.searchData();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.clearSelection();
      this.loadItems();
    },
    goToPage(page) {
      const nextPage = Math.min(Math.max(1, Number(page) || 1), this.totalPages);
      if (nextPage === this.currentPage) {
        return;
      }
      this.currentPage = nextPage;
      this.clearSelection();
      this.loadItems();
    },
    clearSelection() {
      this.selectedRows = [];
    },
    async exportExcel() {
      if (this.exporting) {
        return;
      }
      this.exporting = true;
      this.errorMessage = "";
      try {
        const response = await this.$store.dispatch(
          "aiKey/listSessionsExcel",
          this.buildServerParams()
        );
        const url = response && response.data;
        if (!url || typeof url !== "string") {
          throw new Error("File Excel belum tersedia.");
        }
        this.downloadUrl = url;
        const opened = window.open(url, "_blank");
        if (opened) {
          opened.focus();
        }
      } catch (error) {
        this.errorMessage = this.getErrorMessage(
          error,
          "Export Excel belum dapat dibuat. Coba lagi."
        );
      } finally {
        this.exporting = false;
      }
    },
    openDetail(row) {
      this.closeRowMenu();
      this.detailRow = row;
    },
    closeDetail() {
      this.detailRow = null;
    },
    async openChat(row) {
      if (!row || this.chatOpening) {
        return;
      }
      this.closeRowMenu();
      this.chatOpening = true;
      this.chatOpeningRowId = row.id;
      try {
        await openChatRoomForSession(this.$store, row);
      } catch (error) {
        this.$fire({
          title: "Gagal Membuka Chat Room",
          text: this.getErrorMessage(error, "Chat Room belum dapat dibuka."),
          type: "error"
        });
      } finally {
        this.chatOpening = false;
        this.chatOpeningRowId = "";
      }
    },
    actionConfig(action) {
      const configs = {
        destroy: {
          dispatch: "sessionDestroy",
          title: "Tutup sesi ini?",
          bulkTitle: "Tutup {count} sesi?",
          text: "Sesi akan ditutup dan sistem menyiapkan rangkuman percakapan secara otomatis.",
          bulkText:
            "{count} sesi akan ditutup dan sistem menyiapkan rangkuman percakapan secara otomatis.",
          success: "Sesi ditutup. Rangkuman percakapan sedang disiapkan.",
          confirmText: "Ya, tutup sesi"
        },
        pause: {
          dispatch: "sessionPause",
          title: "Jeda sesi ini?",
          bulkTitle: "Jeda {count} sesi?",
          text: "AI tidak akan merespons otomatis untuk sesi ini sampai dimulai ulang.",
          bulkText:
            "AI tidak akan merespons otomatis untuk {count} sesi ini sampai dimulai ulang.",
          success: "Sesi berhasil dijeda.",
          confirmText: "Ya, jeda sesi"
        },
        start: {
          dispatch: "sessionStart",
          title: "Mulai ulang sesi ini?",
          bulkTitle: "Mulai ulang {count} sesi?",
          text: "Sesi akan diaktifkan kembali dan AI dapat merespons pelanggan.",
          bulkText:
            "{count} sesi akan diaktifkan kembali dan AI dapat merespons pelanggan.",
          success: "Sesi berhasil diaktifkan kembali.",
          confirmText: "Ya, mulai ulang"
        },
        delete: {
          dispatch: "sessionDelete",
          title: "Hapus sesi ini?",
          bulkTitle: "Hapus {count} sesi?",
          text: "Data sesi dan percakapan yang terkait akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.",
          bulkText:
            "{count} sesi beserta data percakapannya akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.",
          success: "Sesi berhasil dihapus.",
          confirmText: "Ya, hapus permanen"
        }
      };
      return configs[action] || null;
    },
    async runAction(action, rows) {
      if (this.actionBusy || !rows || !rows.length) {
        return;
      }
      const config = this.actionConfig(action);
      if (!config) {
        return;
      }

      this.closeRowMenu();
      const isBulk = rows.length > 1;
      const fireOptions = {
        title: isBulk
          ? config.bulkTitle.replace("{count}", String(rows.length))
          : config.title,
        text: isBulk
          ? config.bulkText.replace("{count}", String(rows.length))
          : config.text,
        showCancelButton: true,
        confirmButtonText: config.confirmText,
        cancelButtonText: "Batal",
        type: action === "delete" ? "error" : "warning",
        confirmButtonColor: action === "delete" ? "#d92d20" : "#f39c12"
      };
      const confirmation = await this.$fire(fireOptions);
      if (!confirmation || confirmation.value !== true) {
        return;
      }

      this.actionBusy = true;
      this.processingAction = action;
      this.processingRowIds = rows.map(row => String(row.id));
      try {
        await this.$store.dispatch("aiKey/" + config.dispatch, {
          id: rows.map(row => ({ id: row.id }))
        });
        this.$fire({
          title: "Berhasil",
          text: config.success,
          type: "success",
          timer: 2200
        });
        this.clearSelection();
        this.closeDetail();
        await this.loadItems();
      } catch (error) {
        this.$fire({
          title: "Operasi Gagal",
          text: this.getErrorMessage(error, "Operasi belum berhasil. Coba lagi."),
          type: "error"
        });
      } finally {
        this.actionBusy = false;
        this.processingAction = "";
        this.processingRowIds = [];
      }
    },
    toggleRowMenu(row, event) {
      if (
        this.rowMenu.open &&
        this.rowMenu.row &&
        String(this.rowMenu.row.id) === String(row.id)
      ) {
        this.closeRowMenu();
        return;
      }

      const trigger = event && event.currentTarget ? event.currentTarget : null;
      if (!trigger) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const menuWidth = 220;
      const menuHeight = 250;
      let left = rect.right - menuWidth;
      if (left < 12) {
        left = 12;
      }
      if (left + menuWidth > window.innerWidth - 12) {
        left = window.innerWidth - menuWidth - 12;
      }
      let top = rect.bottom + 8;
      if (top + menuHeight > window.innerHeight - 12) {
        top = Math.max(12, rect.top - menuHeight - 8);
      }

      this.rowMenu = {
        open: true,
        row: row,
        style: {
          top: top + "px",
          left: left + "px",
          width: menuWidth + "px"
        }
      };
    },
    closeRowMenu() {
      if (!this.rowMenu.open) {
        return;
      }
      this.rowMenu = {
        open: false,
        row: null,
        style: {}
      };
    },
    handleDocumentClick() {
      this.closeRowMenu();
    },
    getErrorMessage(error, fallback) {
      const candidates = [
        error && error.message,
        error && error.response && error.response.data && error.response.data.msg
      ];
      for (let index = 0; index < candidates.length; index++) {
        const value = candidates[index];
        if (typeof value === "string" && value.trim() !== "" && value !== "Error") {
          return value.replace(/<br\s*\/?>/gi, " ");
        }
      }
      return fallback;
    }
  }
};
</script>

<style scoped>
.waajo-sessions {
  position: relative;
  padding-bottom: 72px;
  color: #344054;
}

.waajo-session-card {
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.waajo-session-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 18px 14px;
}

.waajo-session-card-head h4 {
  margin: 0 0 5px;
  color: #101828;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.35;
}

.waajo-session-card-head p {
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
}

.waajo-session-count {
  display: inline-flex;
  margin-left: 7px;
  padding: 3px 9px;
  color: #b36b00;
  background: #fff7e8;
  border: 1px solid #f6d49b;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  vertical-align: middle;
}

.waajo-session-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  padding: 0;
  color: #667085;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 9px;
  font-size: 13px;
}

.waajo-session-refresh:hover:not(:disabled) {
  color: #b36b00;
  background: #fffaf1;
  border-color: #f6d49b;
}

.waajo-session-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 9px;
  padding: 0 18px 15px;
}

.waajo-session-search {
  position: relative;
  display: flex;
  flex: 1 1 230px;
  align-items: center;
  min-width: 210px;
  height: 40px;
  padding: 0 11px;
  color: #98a2b3;
  background: #f8fafc;
  border: 1px solid #e2e8f2;
  border-radius: 9px;
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.waajo-session-search:focus-within {
  background: #ffffff;
  border-color: #f6c26b;
  box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.14);
}

.waajo-session-search input {
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0 7px;
  color: #344054;
  background: transparent;
  border: 0;
  outline: none;
  font-size: 13px;
}

.waajo-session-search input::placeholder {
  color: #98a2b3;
}

.waajo-session-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  padding: 0;
  color: #98a2b3;
  background: #eef2f7;
  border: 0;
  border-radius: 50%;
  font-size: 10px;
}

.waajo-session-search-clear:hover {
  color: #475467;
  background: #e2e8f0;
}

.waajo-session-search-btn,
.waajo-session-export,
.waajo-session-reset {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 13px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.waajo-session-search-btn {
  color: #b36b00;
  background: #fff7e8;
  border: 1px solid #f6d49b;
}

.waajo-session-search-btn:hover,
.waajo-session-search-btn:focus {
  color: #8f5600;
  background: #ffefd5;
  border-color: #f39c12;
}

.waajo-session-select {
  height: 40px;
  min-width: 135px;
  padding: 0 28px 0 11px;
  color: #475467;
  background: #ffffff;
  border: 1px solid #e2e8f2;
  border-radius: 9px;
  font-size: 13px;
}

.waajo-session-date-range {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 10px;
  color: #667085;
  background: #ffffff;
  border: 1px solid #e2e8f2;
  border-radius: 9px;
}

.waajo-session-date-range.is-active {
  color: #b36b00;
  background: #fffaf1;
  border-color: #f6d49b;
}

.waajo-session-date-range > i {
  font-size: 13px;
}

.waajo-session-date-range input {
  width: 116px;
  min-width: 0;
  padding: 0;
  color: #344054;
  background: transparent;
  border: 0;
  outline: none;
  font-size: 12px;
}

.waajo-session-date-sep {
  color: #98a2b3;
  font-size: 12px;
}

.waajo-session-date-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  color: #98a2b3;
  background: #eef2f7;
  border: 0;
  border-radius: 50%;
  font-size: 9px;
}

.waajo-session-export {
  color: #b36b00;
  background: #ffffff;
  border: 1px solid #f6d49b;
}

.waajo-session-export:hover:not(:disabled),
.waajo-session-export:focus {
  color: #8f5600;
  background: #fffaf1;
  border-color: #f39c12;
}

.waajo-session-reset {
  height: 34px;
  padding: 0 8px;
  color: #98a2b3;
  background: transparent;
  border: 0;
  font-weight: 600;
  text-decoration: underline;
}

.waajo-session-reset:hover {
  color: #b36b00;
}

.waajo-session-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0 18px 14px;
  padding: 10px 12px;
  color: #475467;
  background: #f8fafc;
  border: 1px solid #e7edf5;
  border-radius: 9px;
  font-size: 13px;
}

.waajo-session-alert.is-success {
  color: #067647;
  background: #ecfdf3;
  border-color: #bbf7d0;
}

.waajo-session-alert.is-error {
  color: #b42318;
  background: #fff7f6;
  border-color: #fecdca;
}

.waajo-session-alert a,
.waajo-session-alert button {
  color: inherit;
  background: transparent;
  border: 0;
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
}

.waajo-session-table-wrap {
  overflow-x: auto;
  overflow-y: hidden;
}

.waajo-session-table {
  min-width: 1180px;
  margin-bottom: 0;
  color: #475467;
  font-size: 14px;
}

.waajo-session-table thead th {
  padding: 12px 12px;
  color: #667085;
  background: #f8fafc;
  border-top: 1px solid #eef2f7;
  border-bottom: 1px solid #e7edf5;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

.waajo-session-table tbody td {
  padding: 14px 12px;
  border-top: 1px solid #f1f4f8;
  vertical-align: middle;
}

.waajo-session-table tbody tr:hover {
  background: #fffdf8;
}

.waajo-session-table tbody tr.is-selected {
  background: #fff7e8;
}

.waajo-session-check-col {
  width: 46px;
  padding-right: 4px !important;
  text-align: center;
}

.waajo-session-check-col input {
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: #f39c12;
}

.waajo-session-nowrap {
  white-space: nowrap;
}

.waajo-session-date,
.waajo-session-end {
  color: #475467;
  font-size: 14px;
}

.waajo-session-source {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
}

.waajo-session-license {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #475467;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.waajo-session-license i {
  color: #f39c12;
  font-size: 13px;
}

.waajo-session-phone {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  color: #344054;
  background: #f8fafc;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease,
    color 0.18s ease;
}

.waajo-session-phone i {
  color: #25d366;
  font-size: 15px;
}

.waajo-session-phone:hover:not(:disabled) {
  color: #067647;
  background: #ecfdf3;
  border-color: #bbf7d0;
}

.waajo-session-phone:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.waajo-session-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border: 1px solid transparent;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.waajo-session-status-dot {
  width: 6px;
  height: 6px;
  background: currentColor;
  border-radius: 50%;
}

.waajo-session-status.is-running {
  color: #067647;
  background: #ecfdf3;
  border-color: #bbf7d0;
}

.waajo-session-status.is-paused {
  color: #b54708;
  background: #fffaeb;
  border-color: #fedf89;
}

.waajo-session-status.is-closed {
  color: #b42318;
  background: #fff1f0;
  border-color: #fecdca;
}

.waajo-session-status-note {
  display: block;
  margin-top: 4px;
  color: #b54708;
  font-size: 12px;
  line-height: 1.35;
}

.waajo-session-message {
  display: -webkit-box;
  max-width: 270px;
  padding: 0;
  overflow: hidden;
  color: #475467;
  background: transparent;
  border: 0;
  font-size: 14px;
  line-height: 1.45;
  text-align: left;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  cursor: pointer;
}

.waajo-session-message:hover {
  color: #b36b00;
}

.waajo-session-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  white-space: nowrap;
}

.waajo-session-actions .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 34px;
  padding: 0 11px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.waajo-session-detail-btn {
  color: #ffffff;
  background: #f39c12;
  border: 1px solid #f39c12;
  box-shadow: 0 3px 8px rgba(243, 156, 18, 0.16);
}

.waajo-session-detail-btn:hover:not(:disabled),
.waajo-session-detail-btn:focus {
  color: #ffffff;
  background: #e08b13;
  border-color: #e08b13;
}

.waajo-session-more-btn {
  color: #b36b00;
  background: #ffffff;
  border: 1px solid #f6d49b;
}

.waajo-session-more-btn:hover:not(:disabled),
.waajo-session-more-btn:focus {
  color: #8f5600;
  background: #fffaf1;
  border-color: #f39c12;
}

.waajo-session-actions .btn:disabled,
.waajo-session-refresh:disabled,
.waajo-session-export:disabled,
.waajo-session-bulk-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.waajo-session-sk {
  display: inline-block;
  height: 12px;
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
  background-size: 400% 100%;
  border-radius: 6px;
  animation: waajo-session-shimmer 1.4s ease infinite;
}

.waajo-session-sk-check {
  width: 15px;
  height: 15px;
  border-radius: 4px;
}

.waajo-session-sk-badge {
  width: 76px;
  height: 22px;
  border-radius: 20px;
}

@keyframes waajo-session-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.waajo-session-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 38px 18px;
  text-align: center;
}

.waajo-session-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 3px;
  color: #f39c12;
  background: #fff7e8;
  border-radius: 12px;
  font-size: 19px;
}

.waajo-session-empty strong {
  color: #344054;
  font-size: 15px;
}

.waajo-session-empty p {
  max-width: 460px;
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.55;
}

.waajo-session-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-top: 1px solid #f1f4f8;
}

.waajo-session-page-size,
.waajo-session-page-info {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #667085;
  font-size: 13px;
}

.waajo-session-page-size select {
  height: 32px;
  padding: 0 24px 0 9px;
  color: #475467;
  background: #ffffff;
  border: 1px solid #e2e8f2;
  border-radius: 7px;
  font-size: 13px;
}

.waajo-session-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.waajo-session-pager button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  color: #475467;
  background: #ffffff;
  border: 1px solid #e2e8f2;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
}

.waajo-session-pager button:hover:not(:disabled) {
  color: #b36b00;
  background: #fff7e8;
  border-color: #f6d49b;
}

.waajo-session-pager button.active {
  color: #ffffff;
  background: #f39c12;
  border-color: #f39c12;
}

.waajo-session-pager button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.waajo-session-pager-ellipsis {
  padding: 0 2px;
  color: #98a2b3;
  font-size: 13px;
}

.waajo-session-bulk {
  position: fixed;
  bottom: 24px;
  left: 50%;
  z-index: 1035;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px;
  color: #ffffff;
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.3);
  transform: translateX(-50%);
}

.waajo-session-bulk-count {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.waajo-session-bulk-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  color: #ffffff;
  background: #f39c12;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
}

.waajo-session-bulk-sep {
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.14);
}

.waajo-session-bulk-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 9px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.waajo-session-bulk-btn:hover:not(:disabled) {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.16);
}

.waajo-session-bulk-btn.is-danger {
  color: #fecaca;
  background: rgba(239, 68, 68, 0.16);
  border-color: rgba(239, 68, 68, 0.3);
}

.waajo-session-bulk-btn.is-danger:hover:not(:disabled) {
  color: #ffffff;
  background: rgba(239, 68, 68, 0.36);
}

.waajo-session-bulk-btn.is-clear {
  color: #94a3b8;
  background: transparent;
  border-color: transparent;
}

.waajo-session-bulk-btn.is-clear:hover:not(:disabled) {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
}

.waajo-session-slide-up-enter-active,
.waajo-session-slide-up-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.waajo-session-slide-up-enter,
.waajo-session-slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 14px);
}

.waajo-session-menu {
  position: fixed;
  z-index: 1055;
  padding: 7px;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 12px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.18);
}

.waajo-session-menu-item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 9px 10px;
  color: #475467;
  background: transparent;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
}

.waajo-session-menu-item i {
  width: 16px;
  color: #667085;
  text-align: center;
}

.waajo-session-menu-item:hover:not(:disabled),
.waajo-session-menu-item:focus {
  color: #b36b00;
  background: #fffaf1;
  outline: none;
}

.waajo-session-menu-item:hover:not(:disabled) i,
.waajo-session-menu-item:focus i {
  color: #b36b00;
}

.waajo-session-menu-item.is-danger {
  color: #b42318;
}

.waajo-session-menu-item.is-danger i {
  color: #d92d20;
}

.waajo-session-menu-item.is-danger:hover:not(:disabled) {
  color: #912018;
  background: #fff1f0;
}

.waajo-session-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.waajo-session-menu-divider {
  height: 1px;
  margin: 5px 4px;
  background: #eef2f7;
}

.waajo-session-modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.46);
}

.waajo-session-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
}

.waajo-session-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f1f4f8;
}

.waajo-session-modal-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: #f39c12;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.waajo-session-modal-head h4 {
  margin: 0;
  color: #101828;
  font-size: 19px;
  font-weight: 700;
}

.waajo-session-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #667085;
  background: #f8fafc;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  font-size: 12px;
}

.waajo-session-modal-close:hover {
  color: #b42318;
  background: #fff1f0;
  border-color: #fecdca;
}

.waajo-session-modal-body {
  padding: 18px 20px;
  overflow-y: auto;
}

.waajo-session-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.waajo-session-detail-item {
  min-width: 0;
  padding: 11px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
}

.waajo-session-detail-item > span:first-child {
  display: block;
  margin-bottom: 3px;
  color: #667085;
  font-size: 12px;
  font-weight: 600;
}

.waajo-session-detail-item strong {
  display: block;
  overflow: hidden;
  color: #344054;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  text-overflow: ellipsis;
}

.waajo-session-detail-section {
  margin-top: 18px;
}

.waajo-session-detail-section h5 {
  margin: 0 0 9px;
  color: #344054;
  font-size: 15px;
  font-weight: 700;
}

.waajo-session-detail-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.waajo-session-detail-section-head small {
  color: #98a2b3;
  font-size: 12px;
}

.waajo-session-transcript {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.waajo-session-bubble {
  padding: 10px 12px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
}

.waajo-session-bubble.is-user {
  background: #f8fafc;
}

.waajo-session-bubble.is-assistant {
  background: #fffaf1;
  border-color: #f6e3bd;
}

.waajo-session-bubble-label {
  display: block;
  margin-bottom: 4px;
  color: #667085;
  font-size: 12px;
  font-weight: 700;
}

.waajo-session-bubble.is-assistant .waajo-session-bubble-label {
  color: #b36b00;
}

.waajo-session-bubble p {
  margin: 0;
  color: #344054;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.waajo-session-detail-empty {
  padding: 14px;
  color: #98a2b3;
  background: #f8fafc;
  border: 1px dashed #e2e8f2;
  border-radius: 10px;
  font-size: 13px;
  text-align: center;
}

.waajo-session-summary {
  margin: 0;
  padding: 12px;
  color: #344054;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.waajo-session-detail-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 18px;
  padding: 10px 12px;
  color: #667085;
  background: #fffaf1;
  border: 1px solid #f6e3bd;
  border-radius: 9px;
  font-size: 12px;
  line-height: 1.55;
}

.waajo-session-detail-note i {
  margin-top: 2px;
  color: #e08b13;
}

.waajo-session-modal-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #f1f4f8;
}

.waajo-session-btn-primary,
.waajo-session-btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
}

.waajo-session-btn-primary {
  color: #ffffff;
  background: #f39c12;
  border: 1px solid #f39c12;
}

.waajo-session-btn-primary:hover:not(:disabled) {
  color: #ffffff;
  background: #e08b13;
  border-color: #e08b13;
}

.waajo-session-btn-secondary {
  color: #475467;
  background: #ffffff;
  border: 1px solid #e2e8f2;
}

.waajo-session-btn-secondary:hover {
  color: #b36b00;
  background: #fffaf1;
  border-color: #f6d49b;
}

.waajo-session-fade-enter-active,
.waajo-session-fade-leave-active {
  transition: opacity 0.18s ease;
}

.waajo-session-fade-enter,
.waajo-session-fade-leave-to {
  opacity: 0;
}

@media (max-width: 991px) {
  .waajo-session-search {
    flex: 1 1 100%;
  }

  .waajo-session-search-btn {
    flex: 0 0 auto;
  }

  .waajo-session-date-range {
    flex: 1 1 100%;
    justify-content: space-between;
  }

  .waajo-session-date-range input {
    flex: 1 1 100px;
    width: auto;
  }

  .waajo-session-footer {
    align-items: flex-start;
  }
}

@media (max-width: 767px) {
  .waajo-session-card-head {
    padding: 15px 14px 12px;
  }

  .waajo-session-toolbar {
    padding: 0 14px 13px;
  }

  .waajo-session-select,
  .waajo-session-export {
    flex: 1 1 auto;
  }

  .waajo-session-bulk {
    bottom: 14px;
    justify-content: center;
    width: calc(100vw - 24px);
  }

  .waajo-session-bulk-count {
    justify-content: center;
    width: 100%;
  }

  .waajo-session-bulk-sep {
    display: none;
  }

  .waajo-session-bulk-btn {
    flex: 1 1 auto;
  }

  .waajo-session-detail-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .waajo-session-modal-backdrop {
    padding: 10px;
  }

  .waajo-session-modal {
    max-height: 94vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/settings/components/AiListSessions.vue